import { FORM_TYPE_CREATE_REPLICATION } from 'js/constants/formTypes';
import { useCallback } from 'react';
import usePaging from 'new/state/paging/repository/usePaging';
import useFormsAdapter from 'new/state/forms/adapter/useFormsAdapter';
import useFlowsSetup from 'new/components/flows/controller/useFlowsSetup';
import flowTypes from 'new/components/flows/types/flowTypes';

export default function useReplication() {
  const {
    createSource: createSourceFromForm,
    updateSource: updateSourceFromForm,
    createSink: createSinkFromForm,
    updateSink: updateSinkFromForm,
  } = useFormsAdapter(FORM_TYPE_CREATE_REPLICATION);
  const { getSourceConfigFields, getMetaInfoById, getAuthFields } = usePaging();

  const {
    onSelectConnection,
    data,
    effectiveConnectionType,
    updateFormData,
    resetFormData,
    end,
    start,
  } = useFlowsSetup(FORM_TYPE_CREATE_REPLICATION, flowTypes.replication);

  const { selectedConnection, credentialId, connectionType } = data || {};

  const { connectionCategory } = getMetaInfoById(selectedConnection) || {};

  const createSource = useCallback(
    async (sourceInfo) => {
      const response: any = await createSourceFromForm({
        credentialId,
        sourceType: selectedConnection,
        connectionCategory,
        shouldActivateSource: true,
        sourceInfo,
      });
      return response?.payload?.formData?.source;
    },
    [createSourceFromForm, credentialId, selectedConnection, connectionCategory],
  );

  const createSink = useCallback(
    async (sinkInfo) => {
      const response: any = await createSinkFromForm({
        formType: FORM_TYPE_CREATE_REPLICATION,
        credentialId,
        sinkType: selectedConnection,
        sinkInfo,
      });
      return response?.payload?.formData?.sink;
    },
    [createSinkFromForm, credentialId, selectedConnection],
  );

  const updateSource = useCallback(
    async (sourceInfo, runWithIngestion = false) => {
      const response: any = await updateSourceFromForm({
        sourceInfo,
        credentialId,
        sourceType: selectedConnection,
        connectionCategory,
        runWithIngestion: runWithIngestion,
      });

      return response?.payload?.formData?.source;
    },
    [updateSourceFromForm, credentialId, selectedConnection, connectionCategory],
  );

  const updateSink = useCallback(
    async (sinkInfo) => {
      const response: any = await updateSinkFromForm({
        sinkInfo,
      });
      return response?.payload?.formData?.sink;
    },
    [updateSinkFromForm],
  );

  return {
    ...data,
    data,
    isEditMode: !!(
      (connectionType === 'SINK' && data.sink?.id) ||
      (connectionType === 'SOURCE' && data.source?.id)
    ),
    startForm: start,
    endForm: end,
    getMetaInfoById,
    getSourceConfigFields,
    connectionCategory,
    getAuthFields,
    createSource,
    createSink,
    updateSource,
    updateSink,
    onSelectConnection: onSelectConnection(flowTypes.replication),
    effectiveConnectionType,
    updateFormData,
    resetFormData,
  };
}
